import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Space,
  Upload,
  Select,
  Typography,
  Segmented,
  Spin,
  message as antMessage,
} from 'antd';
import {
  SaveOutlined,
  PictureOutlined,
  ArrowLeftOutlined,
  AppleOutlined,
  AndroidOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { useAppDispatch, useAppSelector } from 'store';
import {
  createTemplate,
  updateTemplate,
  fetchTemplate,
  selectCurrentTemplate,
  selectPushLoading,
} from 'store/slices/push.slice';
import {
  fetchApplications,
  selectApplications,
} from 'store/slices/applications.slice';
import { PushTemplate } from 'types/push_management.types';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';
import { asString } from 'utils/resolve';
import '../../styles/Common.css';

const { TextArea } = Input;
const { Title, Text: AntText } = Typography;

type DeviceType = 'ios' | 'android';

interface PushPreviewProps {
  title: string;
  body: string;
  imageUrl: string;
  appName: string;
}

const IOSPreview: React.FC<PushPreviewProps> = ({ title, body, imageUrl, appName }) => (
  <div
    style={{
      width: 300,
      margin: '0 auto',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
    }}
  >
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px 8px',
          gap: 10,
        }}
      >
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: 5,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 13, color: '#8e8e93', fontWeight: 500, flex: 1 }}>
          {appName || 'App Name'}
        </span>
        <span style={{ fontSize: 12, color: '#8e8e93' }}>сейчас</span>
      </div>
      <div style={{ padding: '0 16px 14px' }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: '#1c1c1e',
            lineHeight: 1.3,
            marginBottom: 2,
          }}
        >
          {title || 'Заголовок'}
        </div>
        <div
          style={{
            fontSize: 14,
            color: '#3a3a3c',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {body || 'Текст сообщения'}
        </div>
      </div>
      {imageUrl && (
        <div style={{ padding: '0 12px 12px' }}>
          <img
            src={imageUrl}
            alt="Push image"
            style={{
              width: '100%',
              borderRadius: 12,
              objectFit: 'cover',
              maxHeight: 180,
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  </div>
);

const AndroidPreview: React.FC<PushPreviewProps> = ({ title, body, imageUrl, appName }) => (
  <div
    style={{
      width: 300,
      margin: '0 auto',
      fontFamily: '"Roboto", "Google Sans", sans-serif',
    }}
  >
    <div
      style={{
        background: '#ffffff',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 1px 6px rgba(0,0,0,0.1)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px 4px',
          gap: 8,
        }}
      >
        <div
          style={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: '#4285f4',
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 12, color: '#70757a', fontWeight: 400, flex: 1 }}>
          {appName || 'App Name'}
        </span>
        <span style={{ fontSize: 11, color: '#70757a' }}>сейчас</span>
        <span style={{ fontSize: 14, color: '#70757a', marginLeft: 4 }}>▾</span>
      </div>
      <div
        style={{
          padding: '6px 16px 14px',
          display: 'flex',
          gap: 12,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: '#202124',
              lineHeight: 1.3,
              marginBottom: 2,
            }}
          >
            {title || 'Заголовок'}
          </div>
          <div
            style={{
              fontSize: 13,
              color: '#5f6368',
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: imageUrl ? 2 : 6,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {body || 'Текст сообщения'}
          </div>
        </div>
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Thumbnail"
            style={{
              width: 48,
              height: 48,
              borderRadius: 8,
              objectFit: 'cover',
              flexShrink: 0,
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
      </div>
      {imageUrl && (
        <div style={{ padding: '0 16px 12px' }}>
          <img
            src={imageUrl}
            alt="Push image"
            style={{
              width: '100%',
              borderRadius: 12,
              objectFit: 'cover',
              maxHeight: 180,
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  </div>
);
const TemplateForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const isEditMode = Boolean(id);
  const applications = useAppSelector(selectApplications);
  const currentTemplate = useAppSelector(selectCurrentTemplate);
  const loading = useAppSelector(selectPushLoading);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<DeviceType>('ios');
  const [formReady, setFormReady] = useState(!isEditMode);
  const watchedTitle = Form.useWatch('title', form);
  const watchedBody = Form.useWatch('body', form);
  const watchedAppId = Form.useWatch('app_id', form);
  const selectedApp = applications.find(app => app.id === watchedAppId);

  useEffect(() => {
    dispatch(fetchApplications({ limit: 100, offset: 0 }));

    if (isEditMode && id) {
      dispatch(fetchTemplate(parseInt(id)));
    }
  }, [dispatch, isEditMode, id]);

  useEffect(() => {
    if (isEditMode && currentTemplate && id && currentTemplate.id === parseInt(id)) {
      const resolvedImageUrl = asString(currentTemplate.image_url) || '';
      form.setFieldsValue({
        app_id: currentTemplate.app_id,
        name: currentTemplate.name,
        description: currentTemplate.description,
        title: currentTemplate.title,
        body: currentTemplate.body,
        image_url: resolvedImageUrl,
        custom_data: currentTemplate.custom_data
          ? JSON.stringify(currentTemplate.custom_data, null, 2)
          : '',
      });
      if (resolvedImageUrl) {
        setImageUrl(resolvedImageUrl);
      }
      setFormReady(true);
    }
  }, [isEditMode, id, currentTemplate, form]);

  const uploadProps: UploadProps = {
    name: 'file',
    accept: 'image/*',
    showUploadList: false,
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        antMessage.error('Можно загружать только изображения!');
        return false;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        antMessage.error('Размер изображения не должен превышать 2MB!');
        return false;
      }
      return false;
    },
    customRequest: ({ file, onSuccess }) => {
      setUploading(true);

      // TODO: заменить на реальный API загрузки
      setTimeout(() => {
        const fakeUrl = `https://via.placeholder.com/400x200?text=${encodeURIComponent(
          (file as File).name,
        )}`;
        setImageUrl(fakeUrl);
        form.setFieldsValue({ image_url: fakeUrl });
        setUploading(false);
        onSuccess?.('ok');
        antMessage.success('Изображение загружено успешно');
      }, 1000);
    },
  };

  const handleSubmit = async (values: any) => {
    try {
      let customData = null;
      if (values.custom_data) {
        try {
          customData = JSON.parse(values.custom_data);
        } catch {
          antMessage.error('Неверный формат JSON в Custom Data');
          return;
        }
      }

      const templateData: Partial<PushTemplate> = {
        app_id: values.app_id,
        name: values.name,
        description: values.description || '',
        title: values.title,
        body: values.body,
        image_url: asString(values.image_url) || null,
        custom_data: customData,
      };

      if (isEditMode && id) {
        await dispatch(
          updateTemplate({ id: parseInt(id), data: templateData }),
        ).unwrap();
        notify.success('Шаблон успешно обновлен');
      } else {
        await dispatch(createTemplate(templateData as PushTemplate)).unwrap();
        notify.success('Шаблон успешно создан');
      }

      navigate(ROUTES.TEMPLATES);
    } catch (error: any) {
      notify.error(error.message || 'Ошибка при сохранении шаблона');
    }
  };

  if (isEditMode && !formReady) {
    return (
      <div className="page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '100px 0', flexDirection: 'column', gap: 16 }}>
        <Spin size="large" />
        <AntText type="secondary">Загрузка шаблона...</AntText>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(ROUTES.TEMPLATES)}
          >
            Назад
          </Button>
          <Title level={2}>
            {isEditMode ? 'Редактирование шаблона' : 'Создание шаблона'}
          </Title>
        </Space>
        <AntText type="secondary">
          Создайте шаблон для быстрой отправки или использования в автоматизации
        </AntText>
      </div>

      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        <Card style={{ flex: 1, minWidth: 0 }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ custom_data: '' }}
          >
            <Form.Item
              label="Приложение"
              name="app_id"
              rules={[{ required: true, message: 'Выберите приложение' }]}
            >
              <Select
                placeholder="Выберите приложение"
                size="large"
                showSearch
                optionFilterProp="children"
                disabled={isEditMode}
              >
                {applications.map(app => (
                  <Select.Option key={app.id} value={app.id}>
                    {app.name} ({app.bundle_id})
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Название шаблона"
              name="name"
              rules={[
                { required: true, message: 'Введите название' },
                { min: 3, message: 'Минимум 3 символа' },
                { max: 100, message: 'Максимум 100 символов' },
              ]}
            >
              <Input placeholder="Например: Welcome Bonus" size="large" />
            </Form.Item>
            <Form.Item
              label="Описание"
              name="description"
              rules={[{ max: 500, message: 'Максимум 500 символов' }]}
            >
              <TextArea placeholder="Краткое описание шаблона" rows={2} />
            </Form.Item>
            <Form.Item
              label="Заголовок"
              name="title"
              rules={[
                { required: true, message: 'Введите заголовок' },
                { max: 100, message: 'Максимум 100 символов' },
              ]}
            >
              <Input
                placeholder="Добро пожаловать!"
                size="large"
                showCount
                maxLength={100}
              />
            </Form.Item>
            <Form.Item
              label="Текст сообщения"
              name="body"
              rules={[
                { required: true, message: 'Введите текст' },
                { max: 500, message: 'Максимум 500 символов' },
              ]}
            >
              <TextArea
                placeholder="Получите бонус 100% на первый депозит"
                rows={4}
                showCount
                maxLength={500}
              />
            </Form.Item>
            <Form.Item
              label="Изображение"
              name="image_url"
              extra="URL изображения или загрузите файл (опционально)"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Input
                  placeholder="https://example.com/image.jpg"
                  size="large"
                  value={imageUrl}
                  onChange={(e) => {
                    setImageUrl(e.target.value);
                    form.setFieldsValue({ image_url: e.target.value });
                  }}
                />
                <Upload {...uploadProps}>
                  <Button icon={<PictureOutlined />} loading={uploading}>
                    Загрузить изображение
                  </Button>
                </Upload>
              </Space>
            </Form.Item>
            <Form.Item
              label="Custom Data (JSON)"
              name="custom_data"
              extra="Дополнительные данные в формате JSON (опционально)"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    try {
                      JSON.parse(value);
                      return Promise.resolve();
                    } catch {
                      return Promise.reject(new Error('Неверный формат JSON'));
                    }
                  },
                },
              ]}
            >
              <TextArea
                placeholder='{"action": "open_bonus", "promo_code": "WELCOME100"}'
                rows={4}
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, marginTop: 32 }}>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  size="large"
                  loading={loading}
                >
                  {isEditMode ? 'Сохранить изменения' : 'Создать шаблон'}
                </Button>
                <Button
                  size="large"
                  onClick={() => navigate(ROUTES.TEMPLATES)}
                >
                  Отмена
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
        <div style={{ width: 420, flexShrink: 0, position: 'sticky', top: 24 }}>
          <Card
            title="Предпросмотр"
            extra={
              <Segmented
                value={previewDevice}
                onChange={(val) => setPreviewDevice(val as DeviceType)}
                options={[
                  { label: <span><AppleOutlined /> iOS</span>, value: 'ios' },
                  { label: <span><AndroidOutlined /> Android</span>, value: 'android' },
                ]}
                size="small"
              />
            }
          >
            <div
              style={{
                borderRadius: previewDevice === 'ios' ? 40 : 24,
                border: `3px solid ${previewDevice === 'ios' ? '#1d1d1f' : '#3c4043'}`,
                padding: '48px 12px 36px',
                background:
                  previewDevice === 'ios'
                    ? 'linear-gradient(180deg, #1c1c1e 0%, #2c2c2e 100%)'
                    : 'linear-gradient(180deg, #202124 0%, #303134 100%)',
                position: 'relative',
                overflow: 'hidden',
                minHeight: 460,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 12,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 24,
                }}
              >
                {previewDevice === 'ios' ? (
                  <div
                    style={{
                      width: 120,
                      height: 28,
                      background: '#000',
                      borderRadius: 14,
                    }}
                  />
                ) : (
                  <div style={{ fontSize: 12, color: '#9aa0a6' }}>
                    12:00
                  </div>
                )}
              </div>
              <div
                style={{
                  height: 110,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {previewDevice === 'ios' ? (
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: 56,
                        fontWeight: 700,
                        color: '#fff',
                        fontFamily: '-apple-system, "SF Pro Display", sans-serif',
                        letterSpacing: 2,
                        lineHeight: 1,
                      }}
                    >
                      12:00
                    </div>
                    <div
                      style={{
                        fontSize: 16,
                        color: 'rgba(255,255,255,0.7)',
                        marginTop: 4,
                      }}
                    >
                      Понедельник, 2 марта
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: 48,
                        fontWeight: 400,
                        color: '#e8eaed',
                        fontFamily: '"Google Sans", Roboto, sans-serif',
                        lineHeight: 1,
                      }}
                    >
                      12:00
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        color: 'rgba(232,234,237,0.7)',
                        marginTop: 4,
                      }}
                    >
                      Пн, 2 мар.
                    </div>
                  </div>
                )}
              </div>
              <div style={{ padding: '0 4px' }}>
                {previewDevice === 'ios' ? (
                  <IOSPreview
                    title={watchedTitle || ''}
                    body={watchedBody || ''}
                    imageUrl={imageUrl}
                    appName={selectedApp?.name || ''}
                  />
                ) : (
                  <AndroidPreview
                    title={watchedTitle || ''}
                    body={watchedBody || ''}
                    imageUrl={imageUrl}
                    appName={selectedApp?.name || ''}
                  />
                )}
              </div>
              <div
                style={{
                  position: 'absolute',
                  bottom: 8,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: previewDevice === 'ios' ? 134 : 100,
                  height: 5,
                  borderRadius: 3,
                  background: 'rgba(255,255,255,0.3)',
                }}
              />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TemplateForm;